<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { NIcon } from 'naive-ui';
import { IconAdd, IconEdit, IconHelp } from '@/components/design/icons';
import type { ReviewProjectQuestionInDb, ReviewProjectQuestionUpdate } from '@/api';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaPagination from '@/components/design/composite/GaiaPagination.vue';
import GaiaCard from '@/components/design/base/GaiaCard.vue';
import GaiaDeleteButton from '@/components/design/base/GaiaDeleteButton.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaProgressBar from '@/components/design/base/GaiaProgressBar.vue';
import GaiaSearchInput from '@/components/design/base/inputs/GaiaSearchInput.vue';
import GaiaSegmented, {
  type GaiaSegmentedOption,
} from '@/components/design/base/GaiaSegmented.vue';
import GaiaSpinner from '@/components/design/base/GaiaSpinner.vue';
import GaiaStatusChip from '@/components/design/base/GaiaStatusChip.vue';
import GaiaTag from '@/components/design/base/GaiaTag.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaTextarea from '@/components/design/base/inputs/GaiaTextarea.vue';
import GaiaConfirmCard from '@/components/design/composite/GaiaConfirmCard.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaWorkspacePane from '@/components/design/review/GaiaWorkspacePane.vue';
import { gaiaTextButtonOverrides } from '@/components/design/themes/tokens';
import MarkdownRenderer from '@/components/MarkdownRenderer.vue';
import { useReviewProjectWebSocket } from '@/composables/useReviewProjectWebSocket';
import { ReviewProjectService } from '@/services/reviewProjectService';
import { useReviewProjectStore, type QuestionsTableState } from '@/stores/reviewProjectStore';
import { formatDateTimeForTable } from '@/utils/datetimeUtils';
import { useGaiaToast } from '@/composables/useGaiaToast';

const props = defineProps({
  projectUid: {
    type: String,
    required: true,
  },
  /** Expanded, Q&A has the workspace's right column to itself. */
  expanded: {
    type: Boolean,
    default: false,
  },
  /**
   * Passed straight to the pane — a space-separated edge list. The workspace
   * decides it: which edges need a rule depends on whether this pane is sharing
   * its band with a sibling or filling the column alone.
   */
  divider: {
    type: String,
    default: 'top',
  },
});

const emit = defineEmits<{ close: []; 'update:expanded': [expanded: boolean] }>();

const reviewProjectStore = useReviewProjectStore();
const message = useGaiaToast();

const questions = ref<ReviewProjectQuestionInDb[]>([]);
const totalCount = ref<number>(0);
const loading = ref<boolean>(false);

const composerVisible = ref<boolean>(false);
const composerDraft = ref<string>('');
const composerSubmitting = ref<boolean>(false);

// One edit at a time; opening one editor closes the other.
const questionEditUid = ref<string | null>(null);
const questionDraft = ref<string>('');
const questionSaving = ref<boolean>(false);
const answerEditUid = ref<string | null>(null);
const answerDraft = ref<string>('');
const answerSaving = ref<boolean>(false);

/**
 * The destructive action awaiting confirmation, if any. One ref for both:
 * a row can only ever be asking about one of them, and the confirm strip takes
 * the row's place while it does (the pattern GaiaChatHistory uses for its
 * thread deletes).
 */
const confirming = ref<{ uid: string; action: 'delete' | 'clear' } | null>(null);

const expandedUids = ref<Set<string>>(new Set());

async function fetchQuestions(): Promise<void> {
  const state = reviewProjectStore.questionsTableState;
  loading.value = true;
  try {
    const result = await ReviewProjectService.searchQuestions(props.projectUid, {
      text: state.search,
      page: state.page,
      pageSize: state.pageSize,
      answered: state.answeredFilter === 'all' ? undefined : state.answeredFilter === 'answered',
    });
    questions.value = result.results;
    totalCount.value = result.result_count;
    // Deleting the last item of a trailing page leaves it empty — clamp back one page.
    if (!result.results.length && state.page > 1) {
      state.page -= 1;
      await fetchQuestions();
    }
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to load questions.');
    questions.value = [];
    totalCount.value = 0;
  } finally {
    loading.value = false;
  }
}

// The progress strip is decorative; fail silently and keep it hidden.
async function refreshStatistics(): Promise<void> {
  try {
    reviewProjectStore.questionStatistics = await ReviewProjectService.getQuestionStatistics(
      props.projectUid
    );
  } catch {
    reviewProjectStore.questionStatistics = null;
  }
}

/** Mutations change the counts; null the shared cache so the Statistics tab refetches too. */
function invalidateStatistics(): void {
  reviewProjectStore.questionStatistics = null;
  void refreshStatistics();
}

onMounted(() => {
  void fetchQuestions();
  // Always refresh (don't trust the cache): questions created outside this pane
  // now arrive via websocket, but only while it is mounted.
  void refreshStatistics();
});

// Questions can change from anywhere (another client, the review chat's tool);
// the event carries no question data, so refetch list and counts.
useReviewProjectWebSocket({
  projectUid: () => props.projectUid,
  onQuestionsStale: () => {
    void fetchQuestions();
    invalidateStatistics();
  },
});

// The search field owns the debounce and fires this at once when it is cleared.
function applySearch(): void {
  reviewProjectStore.questionsTableState.page = 1;
  void fetchQuestions();
}

function handleFilterChange(value: string): void {
  reviewProjectStore.questionsTableState.answeredFilter =
    value as QuestionsTableState['answeredFilter'];
  reviewProjectStore.questionsTableState.page = 1;
  void fetchQuestions();
}

function handlePageChange(page: number): void {
  reviewProjectStore.questionsTableState.page = page;
  void fetchQuestions();
}

function handlePageSizeChange(pageSize: number): void {
  reviewProjectStore.questionsTableState.pageSize = pageSize;
  reviewProjectStore.questionsTableState.page = 1;
  void fetchQuestions();
}

async function submitQuestion(): Promise<void> {
  const text = composerDraft.value.trim();
  if (!text) return;
  composerSubmitting.value = true;
  try {
    await ReviewProjectService.createQuestion(props.projectUid, { question: text });
    composerDraft.value = '';
    composerVisible.value = false;
    message.success('Question created');
    reviewProjectStore.questionsTableState.page = 1;
    await fetchQuestions();
    invalidateStatistics();
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to create question.');
  } finally {
    composerSubmitting.value = false;
  }
}

function startQuestionEdit(question: ReviewProjectQuestionInDb): void {
  questionEditUid.value = question.uid;
  questionDraft.value = question.question;
  answerEditUid.value = null;
}

async function saveQuestionEdit(): Promise<void> {
  const uid = questionEditUid.value;
  const text = questionDraft.value.trim();
  if (!uid || !text) return;
  questionSaving.value = true;
  try {
    await ReviewProjectService.updateQuestion(props.projectUid, uid, { question: text });
    questionEditUid.value = null;
    message.success('Question updated');
    await fetchQuestions();
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to update question.');
  } finally {
    questionSaving.value = false;
  }
}

function startAnswer(question: ReviewProjectQuestionInDb): void {
  answerEditUid.value = question.uid;
  answerDraft.value = question.answer ?? '';
  questionEditUid.value = null;
}

async function saveAnswer(question: ReviewProjectQuestionInDb): Promise<void> {
  const text = answerDraft.value.trim();
  if (!text) return;
  answerSaving.value = true;
  try {
    // The backend attributes the answer to the requesting user.
    const payload: ReviewProjectQuestionUpdate = { answer: text };
    await ReviewProjectService.updateQuestion(props.projectUid, question.uid, payload);
    answerEditUid.value = null;
    message.success('Answer saved');
    await fetchQuestions();
    invalidateStatistics();
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to save answer.');
  } finally {
    answerSaving.value = false;
  }
}

async function clearAnswer(question: ReviewProjectQuestionInDb): Promise<void> {
  confirming.value = null;
  try {
    await ReviewProjectService.updateQuestion(props.projectUid, question.uid, { answer: null });
    message.success('Question reopened');
    await fetchQuestions();
    invalidateStatistics();
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to clear answer.');
  }
}

async function removeQuestion(question: ReviewProjectQuestionInDb): Promise<void> {
  confirming.value = null;
  try {
    await ReviewProjectService.deleteQuestion(props.projectUid, question.uid);
    message.success('Question deleted');
    await fetchQuestions();
    invalidateStatistics();
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to delete question.');
  }
}

function confirmingAction(
  question: ReviewProjectQuestionInDb,
  action: 'delete' | 'clear'
): boolean {
  return confirming.value?.uid === question.uid && confirming.value.action === action;
}

function toggleExpanded(uid: string): void {
  const next = new Set(expandedUids.value);
  if (next.has(uid)) next.delete(uid);
  else next.add(uid);
  expandedUids.value = next;
}

/** Only long answers get a Show more toggle; short ones never truncate at 4 lines. */
function isLongAnswer(question: ReviewProjectQuestionInDb): boolean {
  const answer = question.answer ?? '';
  return answer.length > 280 || answer.split('\n').length > 4;
}

function askedByLabel(question: ReviewProjectQuestionInDb): string {
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand; empty string must use the fallback
  return question.asked_by_user?.full_name || question.asked_by_user?.email || '—';
}

function answeredByLabel(question: ReviewProjectQuestionInDb): string {
  const user = question.answered_by_user ?? question.answered_by_non_platform_user;
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand; empty string must use the fallback
  return user?.full_name || user?.email || '—';
}

const answeredPercentage = computed(() => {
  const fraction = reviewProjectStore.questionStatistics?.answered_question_percent ?? 0;
  return Number.isFinite(fraction) ? Math.round(fraction * 1000) / 10 : 0;
});

const emptyTitle = computed(() => {
  const state = reviewProjectStore.questionsTableState;
  return state.search || state.answeredFilter !== 'all'
    ? 'No matching questions'
    : 'No questions yet';
});

const emptyDescription = computed(() => {
  const state = reviewProjectStore.questionsTableState;
  return state.search || state.answeredFilter !== 'all'
    ? 'Try a shorter term, or widen the filter.'
    : 'Track what the review still has to find out — ask the first question.';
});

const answeredOptions: GaiaSegmentedOption[] = [
  { label: 'All', value: 'all' },
  { label: 'Open', value: 'open' },
  { label: 'Answered', value: 'answered' },
];

/** How many the current search and filter match — the pager's left-hand label. */
const countLabel = computed(() =>
  totalCount.value === 1 ? '1 question' : `${totalCount.value} questions`
);
</script>

<template>
  <gaia-workspace-pane
    title="Q&amp;A"
    :divider="props.divider"
    pad-body
    expandable
    closable
    :expanded="props.expanded"
    expand-label="Expand over the analysis"
    contract-label="Show the analysis"
    data-tour="rp.qa"
    @update:expanded="value => emit('update:expanded', value)"
    @close="emit('close')"
  >
    <template #icon>
      <n-icon :component="IconHelp" :size="15" />
    </template>

    <!-- Dense controls: this is a pane's toolbar, not a page's, and it shares a
         band with the report — the same size the data room's toolbar uses. -->
    <template #toolbar>
      <n-flex align="center" :size="6" :wrap="false">
        <gaia-search-input
          v-model:value="reviewProjectStore.questionsTableState.search"
          variant="fill"
          size="small"
          placeholder="Search questions and answers…"
          :debounce="500"
          @search="applySearch"
        />
        <gaia-segmented
          :value="reviewProjectStore.questionsTableState.answeredFilter"
          :options="answeredOptions"
          size="small"
          :style="{ flex: 'none' }"
          @update:value="handleFilterChange"
        />
        <gaia-button
          type="primary"
          size="small"
          :style="{ flex: 'none' }"
          @click="composerVisible = !composerVisible"
        >
          <template #icon>
            <n-icon :component="IconAdd" />
          </template>
          Ask question
        </gaia-button>
      </n-flex>
    </template>

    <n-flex vertical :size="12">
      <n-flex
        v-if="(reviewProjectStore.questionStatistics?.total_question_count ?? 0) > 0"
        align="center"
        :size="12"
        :wrap="false"
      >
        <gaia-text size="micro" tone="subtle" :style="{ whiteSpace: 'nowrap' }">
          {{ reviewProjectStore.questionStatistics!.answered_question_count }} /
          {{ reviewProjectStore.questionStatistics!.total_question_count }} answered
        </gaia-text>
        <gaia-progress-bar :percentage="answeredPercentage" :style="{ flex: 1 }" />
      </n-flex>

      <gaia-card v-if="composerVisible" size="small">
        <n-flex vertical :size="8">
          <gaia-textarea
            v-model:value="composerDraft"
            placeholder="Type your question…"
            :autosize="{ minRows: 2, maxRows: 6 }"
          />
          <n-flex justify="end" :size="8">
            <gaia-button quaternary @click="composerVisible = false">Cancel</gaia-button>
            <gaia-button
              type="primary"
              :loading="composerSubmitting"
              :disabled="!composerDraft.trim()"
              @click="submitQuestion"
            >
              Ask
            </gaia-button>
          </n-flex>
        </n-flex>
      </gaia-card>

      <gaia-spinner v-if="loading" :style="{ margin: '32px auto' }" />

      <gaia-empty-state v-else-if="!questions.length" dashed :title="emptyTitle">
        {{ emptyDescription }}
      </gaia-empty-state>

      <template v-else>
        <gaia-card v-for="question in questions" :key="question.uid" size="small">
          <n-flex vertical :size="8">
            <n-flex align="flex-start" :size="8" :wrap="false">
              <n-flex
                v-if="questionEditUid === question.uid"
                vertical
                :size="8"
                :style="{ flex: 1, minWidth: 0 }"
              >
                <gaia-textarea
                  v-model:value="questionDraft"
                  :autosize="{ minRows: 2, maxRows: 6 }"
                />
                <n-flex :size="8">
                  <gaia-button
                    size="small"
                    type="primary"
                    :loading="questionSaving"
                    :disabled="!questionDraft.trim()"
                    @click="saveQuestionEdit"
                  >
                    Save
                  </gaia-button>
                  <gaia-button size="small" quaternary @click="questionEditUid = null">
                    Cancel
                  </gaia-button>
                </n-flex>
              </n-flex>
              <gaia-text
                v-else
                tone="ink"
                weight="medium"
                :style="{ flex: 1, minWidth: 0, whiteSpace: 'pre-wrap' }"
              >
                {{ question.question }}
              </gaia-text>

              <n-flex align="center" :size="6" :wrap="false" :style="{ flex: 'none' }">
                <gaia-tag v-if="question.asked_via_chat" variant="muted" size="tiny">
                  via chat
                </gaia-tag>
                <gaia-tag v-if="question.answered_by_non_platform_user" variant="muted" size="tiny">
                  external
                </gaia-tag>
                <gaia-status-chip :tone="question.answer ? 'success' : 'neutral'">
                  {{ question.answer ? 'Answered' : 'Open' }}
                </gaia-status-chip>
                <gaia-icon-button
                  :dimension="26"
                  title="Edit question"
                  aria-label="Edit question"
                  @click="startQuestionEdit(question)"
                >
                  <n-icon :component="IconEdit" :size="15" />
                </gaia-icon-button>
                <gaia-delete-button
                  subject="question"
                  @click="confirming = { uid: question.uid, action: 'delete' }"
                />
              </n-flex>
            </n-flex>

            <gaia-text size="meta" tone="subtle">
              Asked by {{ askedByLabel(question) }} ·
              {{ formatDateTimeForTable(question.created_at) }}
            </gaia-text>

            <gaia-confirm-card
              v-if="confirmingAction(question, 'delete')"
              confirm-label="Delete"
              @cancel="confirming = null"
              @confirm="removeQuestion(question)"
            >
              Delete this question? Its answer and who gave it go with it.
            </gaia-confirm-card>

            <gaia-confirm-card
              v-else-if="confirmingAction(question, 'clear')"
              confirm-label="Clear answer"
              @cancel="confirming = null"
              @confirm="clearAnswer(question)"
            >
              Clear the answer and reopen this question?
            </gaia-confirm-card>

            <n-flex v-else-if="answerEditUid === question.uid" vertical :size="8">
              <gaia-textarea
                v-model:value="answerDraft"
                placeholder="Write the answer (markdown supported)…"
                :autosize="{ minRows: 3, maxRows: 12 }"
              />
              <n-flex :size="8">
                <gaia-button
                  size="small"
                  type="primary"
                  :loading="answerSaving"
                  :disabled="!answerDraft.trim()"
                  @click="saveAnswer(question)"
                >
                  Save answer
                </gaia-button>
                <gaia-button size="small" quaternary @click="answerEditUid = null">
                  Cancel
                </gaia-button>
              </n-flex>
            </n-flex>

            <n-flex v-else-if="question.answer" vertical :size="6">
              <MarkdownRenderer
                :content="question.answer"
                :max-lines="expandedUids.has(question.uid) ? 0 : 4"
                :show-fade-overlay="!expandedUids.has(question.uid)"
              />
              <n-flex align="center" :size="12">
                <gaia-text size="meta" tone="subtle">
                  Answered by {{ answeredByLabel(question) }} ·
                  {{ formatDateTimeForTable(question.answered_at) }}
                </gaia-text>
                <gaia-button
                  v-if="isLongAnswer(question)"
                  text
                  size="tiny"
                  :theme-overrides="gaiaTextButtonOverrides.ink"
                  @click="toggleExpanded(question.uid)"
                >
                  {{ expandedUids.has(question.uid) ? 'Show less' : 'Show more' }}
                </gaia-button>
                <gaia-button
                  text
                  size="tiny"
                  :theme-overrides="gaiaTextButtonOverrides.ink"
                  @click="startAnswer(question)"
                >
                  Edit answer
                </gaia-button>
                <gaia-button
                  text
                  size="tiny"
                  :theme-overrides="gaiaTextButtonOverrides.ink"
                  @click="confirming = { uid: question.uid, action: 'clear' }"
                >
                  Clear answer
                </gaia-button>
              </n-flex>
            </n-flex>

            <gaia-button
              v-else
              size="small"
              :style="{ alignSelf: 'flex-start' }"
              @click="startAnswer(question)"
            >
              Answer
            </gaia-button>
          </n-flex>
        </gaia-card>
      </template>
    </n-flex>

    <!-- In the pane's footer rather than after the list: the list is what
         scrolls, so a pager sitting under it is only reachable once you have
         scrolled past everything it would take you away from. -->
    <template v-if="questions.length" #footer>
      <n-flex align="center" :size="10" :wrap="false">
        <gaia-text size="micro" tone="subtle">{{ countLabel }}</gaia-text>
        <gaia-pagination
          :page="reviewProjectStore.questionsTableState.page"
          :page-size="reviewProjectStore.questionsTableState.pageSize"
          :total="totalCount"
          :page-sizes="[10, 20, 50]"
          :style="{ marginLeft: 'auto', flex: 'none' }"
          @update:page="handlePageChange"
          @update:page-size="handlePageSizeChange"
        />
      </n-flex>
    </template>
  </gaia-workspace-pane>
</template>
